<template>
  <div ref="rootEl" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
    :class="highlight ? 'versions-highlight' : ''">
    <div class="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
      <p class="text-xs font-semibold text-gray-700 dark:text-gray-200">
        Versions
        <span v-if="!disabled && !loading" class="font-normal text-gray-400 dark:text-gray-500">({{ versions.length }})</span>
        <span v-if="highlight" class="ml-1.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400">New — add one now</span>
      </p>
      <button v-if="!disabled && !versionForm.open" type="button" @click="openForm"
        class="flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
        </svg>
        Add
      </button>
    </div>

    <!-- Disabled preview: no catalog entry to attach a version to yet, so show
         the same form shape inert (nothing to submit) rather than an empty
         box. Same single-row rhythm as the live form below (Version/Released/
         End of life side by side, not stacked), so nothing shifts shape once
         it becomes usable. -->
    <div v-if="disabled" class="px-4 py-3">
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        <div>
          <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Version <span class="normal-case font-normal">(optional)</span></label>
          <input type="text" placeholder="e.g. 16.4" disabled
            class="w-full h-[31px] px-2.5 py-0 text-sm leading-4 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500 placeholder-gray-400 dark:placeholder-gray-500 cursor-not-allowed" />
        </div>
        <div>
          <label class="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Released <span class="normal-case font-normal">(optional)</span></label>
          <DatePicker model-value="" placeholder="Released" disabled input-class="h-[31px] px-2.5 py-0 text-sm leading-4 rounded-md" />
        </div>
        <div>
          <div class="flex items-center justify-between gap-2 mb-1">
            <label class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">End of life <span class="normal-case font-normal">(optional)</span></label>
            <label class="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
              <input type="checkbox" disabled class="w-3 h-3 rounded border-gray-300 dark:border-gray-600 cursor-not-allowed" />
              Not announced
            </label>
          </div>
          <DatePicker model-value="" placeholder="End of life" disabled input-class="h-[31px] px-2.5 py-0 text-sm leading-4 rounded-md" />
        </div>
      </div>
      <p class="text-xs text-amber-600 dark:text-amber-400 font-medium pt-2">Save this component to start tracking versions.</p>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="animate-pulse px-4 py-3 space-y-1.5">
      <div class="h-6 bg-gray-100 dark:bg-gray-800 rounded"></div>
      <div class="h-6 bg-gray-100 dark:bg-gray-800 rounded"></div>
    </div>

    <template v-else>
      <!-- Add flow: a fresh form with nothing to replace yet, shown above the
           list. Editing an existing version instead replaces that row in
           place, below — never both at once. -->
      <div v-if="versionForm.open && !versionForm.editingId" class="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <GlobalComponentVersionForm :form="versionForm" @save="submitVersion" @cancel="closeForm" />
      </div>

      <!-- Search — matches version string or submitted-by, client-side (the
           full list for one component is already loaded, no need to round-trip). -->
      <div v-if="versions.length" class="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
        <div class="relative max-w-[220px]">
          <svg class="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search versions…"
            class="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-[#0d1117] text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          <button v-if="search" type="button" @click="search = ''"
            class="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Existing versions: sortable table, same convention as the catalog
           table's own column headers, with EOL health badges and row tinting
           (reusing the shared versionEolStatus utility) so an expiring or
           expired version is obvious at a glance, not just on hover. Pending
           versions always float to the top regardless of the active column
           sort — they're the ones needing review. -->
      <div v-if="sortedVersions.length" class="overflow-x-auto">
        <table class="w-full text-xs border-collapse">
          <thead>
            <tr class="bg-gray-50/70 dark:bg-black/10 border-b border-gray-100 dark:border-gray-700">
              <th v-for="col in columns" :key="col.field" @click="toggleSort(col.field)"
                class="px-3 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                :class="HIDDEN_CLASSES[col.hidden]">
                <span class="inline-flex items-center gap-1">
                  {{ col.label }}
                  <svg class="w-2.5 h-2.5 flex-shrink-0" :class="sortBy === col.field ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600'"
                    fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                    <path v-if="sortBy === col.field && sortOrder === 'asc'" stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                  </svg>
                </span>
              </th>
              <th class="px-3 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">Submitted by</th>
              <th class="px-3 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap">Organizations</th>
              <th class="px-3 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider text-right whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <template v-for="v in sortedVersions" :key="v.id">
              <!-- Editing this row: the form replaces it entirely, spanning every column. -->
              <tr v-if="versionForm.open && versionForm.editingId === v.id">
                <td colspan="6" class="p-2">
                  <GlobalComponentVersionForm :form="versionForm" :current-status="v.status"
                    @save="submitVersion" @cancel="closeForm" @change-status="(s) => changeStatus(v, s)" />
                </td>
              </tr>
              <tr v-else class="group hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors" :class="rowStatusBg(v)">
                <td class="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{{ v.version }}</td>
                <td class="px-3 py-2.5 whitespace-nowrap" :class="v.released_date ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">
                  {{ v.released_date ? formatDate(v.released_date) : 'Not specified' }}
                </td>
                <td class="px-3 py-2.5">
                  <div class="flex items-center gap-1.5">
                    <span class="whitespace-nowrap" :class="v.eol_date ? VERSION_EOL_TEXT_CLASSES[eolStatus(v.eol_date)] : 'text-gray-300 dark:text-gray-600 italic'">
                      {{ v.eol_date ? formatDate(v.eol_date) : (v.eol_date_not_announced ? 'Not announced' : 'Not specified') }}
                    </span>
                    <span v-if="eolStatus(v.eol_date) !== 'none'" class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold" :class="VERSION_EOL_BADGE_CLASSES[eolStatus(v.eol_date)]">
                      {{ VERSION_EOL_BADGE_LABELS[eolStatus(v.eol_date)] }}
                    </span>
                  </div>
                </td>
                <td class="px-3 py-2.5 hidden md:table-cell">
                  <span class="whitespace-nowrap" :class="v.added_by_org_name ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-600 italic'">
                    {{ v.added_by_org_name || 'Curated' }}
                  </span>
                </td>
                <td class="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-300">{{ v.organizations_count ?? 0 }}</td>
                <td class="px-3 py-2.5 text-right">
                  <div class="flex items-center justify-end gap-1.5">
                    <!-- Read-only here — changing status now lives in the Edit
                         form (see GlobalComponentVersionForm), not as a
                         standing row-level control. -->
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" :class="statusBadgeClass(v.status)">
                      {{ STATUS_LABELS[v.status] }}
                    </span>
                    <button type="button" title="Edit" @click="openEditForm(v)"
                      class="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors opacity-0 group-hover:opacity-100">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p v-else-if="!versionForm.open && versions.length" class="px-4 py-4 text-xs text-gray-400 dark:text-gray-500 text-center">No versions match your search.</p>
      <p v-else-if="!versionForm.open" class="px-4 py-4 text-xs text-gray-400 dark:text-gray-500 text-center">No versions tracked yet.</p>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { useAdminGlobalComponentVersionsStore } from 'stores/admin_global_component_versions.js'
import DatePicker from 'controllers/vue_components/shared/DatePicker.vue'
import GlobalComponentVersionForm from 'controllers/vue_components/admin/global_components/GlobalComponentVersionForm.vue'
// Shared EOL classification — also used by the Applications factsheet's
// Information/Dependencies sections, so "Expired"/"Expiring soon" wording
// and thresholds never drift out of sync across the app. Pure function over
// a raw date string, no org/application coupling, safe to reuse here.
import { eolStatus, VERSION_EOL_TEXT_CLASSES, VERSION_EOL_BADGE_CLASSES, VERSION_EOL_BADGE_LABELS }
  from 'controllers/vue_components/organizations/applications/versionEolStatus.js'

const props = defineProps({
  // Empty until the parent GlobalComponent has actually been saved — see `disabled`.
  globalComponentId: { type: String, default: '' },
  // True for the brief window right after the parent component was just
  // created — draws attention with a pulsing ring and opens the add form
  // straight away, since this is the first moment a version can be attached.
  highlight: { type: Boolean, default: false },
})

const emit = defineEmits(['notify', 'version-status-changed'])

const store = useAdminGlobalComponentVersionsStore()

// No catalog entry to attach a version to yet (still creating one) — render
// an inert preview of the form instead of hitting the API.
const disabled = computed(() => !props.globalComponentId)

const entry = computed(() => store.entryFor(props.globalComponentId))
const versions = computed(() => entry.value.records)
const loading = computed(() => entry.value.loading)

const rootEl = ref(null)
const workingId = ref(null)

// Single shared form state for both the Add flow and the in-place Edit flow
// — only one is ever open at a time. Mirrors DependenciesSection.vue's
// attachForm/InformationSection.vue's versionForm pattern.
const versionForm = ref({
  open: false, editingId: null, version: '', releasedDate: '', eolDate: '', eolDateNotAnnounced: false, saving: false, error: '',
})

const localDateError = computed(() =>
  !!versionForm.value.releasedDate && !!versionForm.value.eolDate && versionForm.value.releasedDate >= versionForm.value.eolDate)

// Tailwind's build-time scanner needs literal class strings, not
// interpolated ones — mirrors VersionsPanel.vue's identical HIDDEN_CLASSES lookup.
const HIDDEN_CLASSES = { md: 'hidden md:table-cell' }

const columns = [
  { field: 'version', label: 'Version' },
  { field: 'released_date', label: 'Released' },
  { field: 'eol_date', label: 'End of life' },
]

const sortBy = ref('version')
const sortOrder = ref('desc')

function toggleSort(field) {
  if (sortBy.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = field
    sortOrder.value = 'asc'
  }
}

// Dates: nulls always sort last, regardless of direction — same convention
// as VersionsPanel.vue's compareDates.
function compareDates(a, b, dir) {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return (new Date(a) - new Date(b)) * dir
}

const search = ref('')

// Client-side — the full list for one component is already loaded, no need
// to round-trip for what's typically a handful of rows.
const searchedVersions = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) return versions.value
  return versions.value.filter((v) =>
    v.version.toLowerCase().includes(query) || (v.added_by_org_name || '').toLowerCase().includes(query))
})

const sortedVersions = computed(() => {
  const dir = sortOrder.value === 'asc' ? 1 : -1
  return [...searchedVersions.value].sort((a, b) => {
    // Pending versions always float to the top — regardless of the active
    // column sort — since they're the ones actually needing review.
    const pendingDiff = (b.status === 'pending') - (a.status === 'pending')
    if (pendingDiff !== 0) return pendingDiff

    switch (sortBy.value) {
      case 'released_date':
      case 'eol_date':
        return compareDates(a[sortBy.value], b[sortBy.value], dir)
      default:
        return a.version.localeCompare(b.version, undefined, { numeric: true, sensitivity: 'base' }) * dir
    }
  })
})

// Row background tint by EOL health — same convention (and color values) as
// DependenciesSection.vue's depRowStatusBg. A version explicitly marked "not
// announced" has no eol_date at all (see the model's
// clear_eol_date_when_not_announced), so eolStatus() alone would read it as
// 'none' (no tint) — the explicit check ahead of it treats it as healthy
// instead, same as DependenciesSection.
const ROW_BG_CLASSES = {
  none: '',
  healthy: 'bg-green-50/40 dark:bg-green-900/10',
  warning: 'bg-amber-50/40 dark:bg-amber-900/10',
  expired: 'bg-red-50/50 dark:bg-red-900/10',
}
function rowStatusBg(v) {
  if (v.eol_date_not_announced) return ROW_BG_CLASSES.healthy
  return ROW_BG_CLASSES[eolStatus(v.eol_date)]
}

const STATUS_LABELS = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', org_specific: 'Org specific' }
const STATUS_BADGE_CLASSES = {
  pending: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  approved: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
  rejected: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  org_specific: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
}
function statusBadgeClass(status) {
  return STATUS_BADGE_CLASSES[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-500'
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function resetForm() {
  versionForm.value = {
    open: versionForm.value.open, editingId: null, version: '', releasedDate: '', eolDate: '', eolDateNotAnnounced: false, saving: false, error: '',
  }
}

function openForm() {
  resetForm()
  versionForm.value.open = true
}

function openEditForm(v) {
  versionForm.value = {
    open: true,
    editingId: v.id,
    version: v.version,
    releasedDate: v.released_date ? String(v.released_date).slice(0, 10) : '',
    eolDate: v.eol_date ? String(v.eol_date).slice(0, 10) : '',
    eolDateNotAnnounced: !!v.eol_date_not_announced,
    saving: false,
    error: '',
  }
}

function closeForm() {
  versionForm.value.open = false
  versionForm.value.editingId = null
}

async function submitVersion() {
  const f = versionForm.value
  if (!f.version.trim() || localDateError.value) return

  f.saving = true
  f.error = ''

  const payload = {
    version: f.version.trim(),
    released_date: f.releasedDate || '',
    eol_date: f.eolDateNotAnnounced ? '' : (f.eolDate || ''),
    eol_date_not_announced: f.eolDateNotAnnounced,
  }

  const res = f.editingId
    ? await store.update(props.globalComponentId, f.editingId, payload)
    : await store.create(props.globalComponentId, payload)

  f.saving = false

  if (!res.js_error && res.response.success) {
    const wasEditing = !!f.editingId
    closeForm()
    emit('notify', wasEditing ? 'Version updated.' : 'Version added.', 'notice')
  } else {
    const validationErrors = res.error?.data?.data
    f.error = Array.isArray(validationErrors) && validationErrors.length
      ? validationErrors.join(', ')
      : 'Something went wrong. Please try again.'
  }
}

const STATUS_ACTIONS = { pending: 'unapprove', approved: 'approve', rejected: 'reject' }

async function changeStatus(v, newStatus) {
  if (newStatus === v.status) return

  const previousStatus = v.status
  workingId.value = v.id
  try {
    const res = await store.setStatus(props.globalComponentId, v.id, STATUS_ACTIONS[newStatus])
    if (res.js_error || !res.response.success) {
      emit('notify', 'Something went wrong.', 'alert')
    } else {
      emit('notify', `Version ${v.version} ${newStatus === 'pending' ? 'reset to pending' : newStatus}.`, 'notice')
      // Lets the page-level "Pending Versions" header count stay in sync
      // without a full refetch — see GlobalComponentsPage's handler.
      emit('version-status-changed', { previousStatus, newStatus })
    }
  } finally {
    workingId.value = null
  }
}

function activate() {
  store.fetchIfStale(props.globalComponentId)

  if (props.highlight) {
    rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    openForm()
  }
}

onMounted(() => {
  if (!disabled.value) activate()
})

// The parent keeps this section mounted throughout the Add flow (so the
// disabled preview above can render), so globalComponentId can go from empty
// to a real id without a remount — pick that transition up here instead of
// relying on onMounted a second time.
watch(() => props.globalComponentId, (newId, oldId) => {
  if (newId && !oldId) activate()
})
</script>

<style scoped>
/* Mirrors OrgComponentVersionsSection.vue's own .versions-highlight pulse —
   same ring + background fade, so a just-created catalog entry's Versions
   section doesn't go unnoticed here either. */
.versions-highlight {
  animation: versions-highlight-pulse 3.5s ease-out;
}
@keyframes versions-highlight-pulse {
  0% {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.55);
    background-color: rgba(37, 99, 235, 0.07);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
    background-color: transparent;
  }
}
</style>
